import { useMemo, useRef, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '../../../i18n/LanguageContext'

const FORMAT_TO_MIME = {
  JPG: ['image/jpeg', 'image/jpg'],
  PNG: ['image/png'],
  WEBP: ['image/webp'],
  GIF: ['image/gif'],
  SVG: ['image/svg+xml'],
  AVIF: ['image/avif'],
  TIFF: ['image/tiff'],
  HEIC: ['image/heic', 'image/heif'],
}
const FORMAT_TO_EXT = {
  JPG: ['.jpg', '.jpeg'],
  PNG: ['.png'],
  WEBP: ['.webp'],
  GIF: ['.gif'],
  SVG: ['.svg'],
  AVIF: ['.avif'],
  TIFF: ['.tif', '.tiff'],
  HEIC: ['.heic', '.heif'],
}

const ASPECT_RATIO_VALUES = {
  any: null, '1:1': 1, '4:3': 4 / 3, '16:9': 16 / 9, '3:2': 3 / 2,
}
const QUALITY_VALUES = { original: null, high: 0.8, medium: 0.6, low: 0.4 }

const readImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight, url }); }
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); }
  img.src = url
})

const compressImage = (file, quality, mimeType) => new Promise((resolve) => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0)
    canvas.toBlob((blob) => {
      URL.revokeObjectURL(url)
      if (!blob) { resolve(file); return }
      const newName = file.name.replace(/\.[^.]+$/, '.jpg')
      resolve(new File([blob], newName, { type: mimeType }))
    }, mimeType, quality)
  }
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
  img.src = url
})

const CustomImageField = ({ field, value, onChange, error, aispaceColor }) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [localError, setLocalError] = useState(null)

  let cfg = {}
  try { cfg = field.options ? JSON.parse(field.options) : {} } catch { cfg = {} }

  const acceptedFormats = Array.isArray(cfg.acceptedFormats) ? cfg.acceptedFormats : ['JPG', 'PNG']
  const multiple = !!cfg.multiple
  const maxCount = multiple ? (Number(cfg.maxCount) || 1) : 1
  const aspectCode = cfg.aspectRatio || 'any'
  const aspectRatio = ASPECT_RATIO_VALUES[aspectCode]
  const minWidth = Number(cfg.minWidth) || 0
  const minHeight = Number(cfg.minHeight) || 0
  const quality = QUALITY_VALUES[cfg.quality]

  const acceptAttr = acceptedFormats
    .flatMap((fmt) => [...(FORMAT_TO_EXT[fmt] || []), ...(FORMAT_TO_MIME[fmt] || [])])
    .join(',')

  const files = Array.isArray(value) ? value : []
  const firstFile = files[0]
  // Object URL for the first file is regenerated whenever it changes — used for
  // the round avatar preview, mirroring the default Project Picture UI.
  const previewUrl = useMemo(() => {
    if (!firstFile) return null
    return URL.createObjectURL(firstFile)
  }, [firstFile])

  const validateAndAdd = async (incoming) => {
    setLocalError(null)
    const list = Array.from(incoming)
    if (files.length + list.length > maxCount) {
      setLocalError(t('projectSetup.errMaxCount', { count: maxCount }))
      return
    }
    const accepted = []
    for (const f of list) {
      // 1. Format check
      const matched = acceptedFormats.some((fmt) => {
        const mimes = FORMAT_TO_MIME[fmt] || []
        if (mimes.includes(f.type)) return true
        const exts = FORMAT_TO_EXT[fmt] || []
        const lower = f.name.toLowerCase()
        return exts.some((ext) => lower.endsWith(ext))
      })
      if (!matched) {
        setLocalError(t('projectSetup.errFormat', { name: f.name }))
        return
      }
      // 2. Dimensions / aspect ratio (against original)
      if (!f.type.includes('svg')) {
        try {
          const dim = await readImage(f)
          if (minWidth && dim.width < minWidth) {
            setLocalError(t('projectSetup.errMinDim', { name: f.name, w: minWidth, h: minHeight || '?' }))
            URL.revokeObjectURL(dim.url); return
          }
          if (minHeight && dim.height < minHeight) {
            setLocalError(t('projectSetup.errMinDim', { name: f.name, w: minWidth || '?', h: minHeight }))
            URL.revokeObjectURL(dim.url); return
          }
          if (aspectRatio !== null && aspectRatio !== undefined) {
            const actual = dim.width / dim.height
            if (Math.abs(actual - aspectRatio) > 0.05) {
              setLocalError(t('projectSetup.errAspect', { name: f.name, ratio: aspectCode }))
              URL.revokeObjectURL(dim.url); return
            }
          }
          URL.revokeObjectURL(dim.url)
        } catch { /* image load failed; fall through */ }
      }
      // 3. Apply quality compression if configured (no size limit enforced —
      //    users can upload images of any size, matching the default Project
      //    Picture upload behaviour).
      let finalFile = f
      if (quality !== null && quality !== undefined && !f.type.includes('svg') && !f.type.includes('gif')) {
        try { finalFile = await compressImage(f, quality, 'image/jpeg') } catch { /* keep original */ }
      }
      accepted.push(finalFile)
    }
    onChange([...files, ...accepted])
  }

  const handleSelect = (e) => {
    const chosen = e.target.files
    if (chosen && chosen.length > 0) validateAndAdd(chosen)
    e.target.value = ''
  }

  const handleDelete = () => {
    setLocalError(null)
    onChange([])
  }

  const displayError = error || localError
  const hasImage = files.length > 0

  return (
    <div>
      <label className="block text-[14px] font-semibold text-[#111827] mb-3">
        {field.field_label}
        {field.required && <span className="text-[#EF4444] ml-1">*</span>}
      </label>
      <div className="flex items-center gap-4">
        {/* Preview — same round avatar as default Project Picture */}
        <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-[#E0F2FE] via-[#DBEAFE] to-[#EDE9FE] flex items-center justify-center overflow-hidden flex-shrink-0">
          {hasImage && previewUrl ? (
            <img src={previewUrl} alt={firstFile?.name || ''} className="w-full h-full object-cover" />
          ) : (
            <svg className="w-10 h-10 opacity-80" viewBox="0 0 40 40" fill="none">
              <path d="M20 10L10 18L20 26L30 18L20 10Z" fill="url(#cifGrad1)" />
              <path d="M10 24L20 32L30 24" stroke="url(#cifGrad2)" strokeWidth="2" strokeLinecap="round"/>
              <defs>
                <linearGradient id="cifGrad1" x1="10" y1="10" x2="30" y2="26" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#38BDF8"/><stop offset="1" stopColor="#818CF8"/>
                </linearGradient>
                <linearGradient id="cifGrad2" x1="10" y1="24" x2="30" y2="32" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#60A5FA"/><stop offset="1" stopColor="#A78BFA"/>
                </linearGradient>
              </defs>
            </svg>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          multiple={multiple && (maxCount - files.length) > 1}
          onChange={handleSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90"
          style={{ backgroundColor: aispaceColor }}
          disabled={files.length >= maxCount}
        >
          {hasImage ? t('createProjectModal.uploadNewImage') : t('createProjectModal.uploadImage')}
        </button>

        {hasImage ? (
          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2.5 text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626] transition-colors"
          >
            {t('common.delete')}
          </button>
        ) : (
          <span className="text-[14px] text-[#9CA3AF]">{t('createProjectModal.noFileSelected')}</span>
        )}
      </div>

      {/* Multi-image extra previews (shown when admin allowed >1) */}
      {files.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {files.slice(1).map((f, idx) => {
            const u = URL.createObjectURL(f)
            return (
              <img
                key={`${f.name}-${idx}`}
                src={u}
                alt={f.name}
                className="w-12 h-12 rounded-lg object-cover border border-[#E5E7EB]"
                title={f.name}
              />
            )
          })}
        </div>
      )}

      {field.helper_text && !displayError && (
        <p className="text-[12px] text-[#6B7280] mt-2">{field.helper_text}</p>
      )}
      {displayError && (
        <div className="flex items-start gap-1 text-[12px] text-[#EF4444] mt-2">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          <span>{displayError}</span>
        </div>
      )}
    </div>
  )
}

export default CustomImageField
